import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || ''}/api`
});

// Module-level variable to store auth callback
let authCallback = null;

// Register callback for auth state clearing
export function registerAuthCallback(callback) {
	if (callback !== null && typeof callback !== 'function') {
		throw new TypeError('registerAuthCallback expects a function or null');
	}
	authCallback = callback;
}

// Request interceptor: add x-admin-token if present
api.interceptors.request.use(config => {
	const token = localStorage.getItem('admin_token');
	if (token) {
		config.headers['x-admin-token'] = token;
	}
	return config;
});

// Response interceptor: call auth callback or fallback to direct handling
api.interceptors.response.use(
	response => response,
	error => {
		if (error.response && error.response.status === 401) {
			if (authCallback) {
				// Use registered callback from AuthContext
				authCallback();
			} else {
				// Fallback: direct localStorage manipulation (shouldn't happen after AuthProvider mounts)
				localStorage.removeItem('admin_token');
				if (!window.location.pathname.includes('/admin/login')) {
					window.location.href = '/admin/login';
				}
			}
		}
		return Promise.reject(error);
	}
);

export function getPhotos(categoryId, offset = null, limit = null, filters = {}) {
	const params = {};
	if (categoryId) params.category = categoryId;
	if (offset !== null) params.offset = offset;
	if (limit !== null) params.limit = limit;

	// Merge additional filters (e.g., show_on_homepage)
	Object.assign(params, filters);

	// Public endpoint now supports pagination
	return api.get('/photos', { params });
}

export function getPhoto(id) {
	return api.get(`/photos/${id}`);
}

export function getCategories() {
	return api.get('/categories');
}

export function submitInquiry(data) {
	return api.post('/inquiries', data);
}

export function adminLogin(username, password) {
	return api.post('/admin/login', { username, password });
}

export function adminLogout() {
	return api.post('/admin/logout');
}

export function adminChangePassword(currentPassword, newPassword) {
	return api.post('/admin/change-password', { currentPassword, newPassword });
}

export function adminRecoverPassword(recoveryCode, newPassword) {
	return api.post('/admin/recover-password', { recoveryCode, newPassword });
}

export function uploadPhoto(formData) {
	return api.post('/photos', formData);
}

export function deletePhoto(id) {
	return api.delete(`/photos/${id}`);
}

export function addCategory(name) {
	return api.post('/categories', { name });
}

export function deleteCategory(id) {
	return api.delete(`/categories/${id}`);
}

export function updateCategory(id, data) {
	return api.patch(`/categories/${id}`, data);
}

export function getInquiries(params) {
	return api.get('/inquiries', { params });
}

export function updateInquiry(id, data) {
	return api.patch(`/inquiries/${id}`, data);
}

export function getTestimonials(categoryId) {
	return api.get('/testimonials', { params: categoryId ? { category: categoryId } : {} });
}

export function submitTestimonial(data) {
	return api.post('/testimonials', data);
}

// Admin photo endpoints
export function getAdminPhotos(categoryId, offset = 0, limit = 20) {
	const params = {};
	if (categoryId) params.category = categoryId;
	params.offset = offset;
	params.limit = limit;

	return api.get('/photos/admin/all', { params });
}

export function bulkUploadPhotos(formData, signal) {
	return api.post('/photos/admin/bulk-upload', formData, {
		signal
	});
}

export function bulkUpdateCategory(photoIds, categoryId) {
	return api.patch('/photos/admin/bulk-category', { photo_ids: photoIds, category_id: categoryId });
}

// Admin category endpoints
export function getAdminCategories() {
	return api.get('/categories/admin/all');
}

// Google Business review page URL
export const GOOGLE_REVIEW_URL = 'https://g.page/r/PLACEHOLDER/review';

// ── About page ────────────────────────────────────────────────────────────

export function getAboutContent() {
  return api.get('/about').then(r => r.data);
}

export function updateAboutBio(bio_html) {
  return api.put('/about/admin/bio', { bio_html }).then(r => r.data);
}

export function uploadTriptychPhoto(position, formData) {
  return api.post(`/about/admin/triptych/${position}`, formData).then(r => r.data);
}

export function deleteTriptychPhoto(position) {
  return api.delete(`/about/admin/triptych/${position}`).then(r => r.data);
}

export function togglePhotoAboutStyle(id) {
  return api.patch(`/photos/${id}/about-style`).then(r => r.data);
}

export function trackPageVisit(sessionId, page) {
  return api.post('/analytics/track', { sessionId, page });
}

export function getAnalyticsSummary(days = 30) {
  return api.get('/analytics/summary', { params: { days } });
}
